/**
 * AdminGlobalHeaderPage.jsx — HustleSuite Admin ▸ Global Header.
 *
 * Configures the "Other Modules" dropdown that ships in every module's
 * global header. Admins can:
 *   • Toggle each module's visibility in the dropdown
 *   • Rename the Menu Name (label shown to visitors)
 *   • Edit the Salespage URL (host — where clicking the item takes them)
 *   • Toggle the "Join Hustle Membership" CTA on each sales page and
 *     change the signup route the CTA links to
 *
 * All rows come from ``GET /api/platform/modules/admin`` and are
 * persisted via ``PATCH /api/platform/modules/{key}``. Changes propagate
 * live — the ModulesMenu snippet clears its cache on the
 * `hs:modules-changed` window event.
 *
 * Route: /admin/global-header
 */
import { useEffect, useState } from "react";
import PlatformHeader from "@/components/PlatformHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Grid3x3, Save, Check, RotateCcw, UserPlus } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

export default function AdminGlobalHeaderPage() {
  const [modules, setModules] = useState([]);
  const [drafts, setDrafts] = useState({});   // { key: { label, host, enabled } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // ── Sign-up CTA controls ───────────────────────────────────────
  // Powered by GET/POST /api/hustlemeet-settings — controls per-page
  // "Join Hustle Membership" tick boxes + the signup route the CTA
  // links to. `ctaDirty` flips as soon as any field is touched.
  const [ctaRoute, setCtaRoute]   = useState("/signup");
  const [ctaVis, setCtaVis]       = useState({});   // { page_key: bool }
  const [ctaSaved, setCtaSaved]   = useState({ route: "/signup", vis: {} });
  const [ctaSaving, setCtaSaving] = useState(false);
  const ctaDirty =
    ctaRoute !== ctaSaved.route ||
    Object.keys({ ...ctaVis, ...ctaSaved.vis }).some(
      (k) => Boolean(ctaVis[k]) !== Boolean(ctaSaved.vis[k]),
    );

  const CTA_PAGES = [
    ["meetups_overview",   "HustleMeetups · Overview"],
    ["meetups_circles",    "HustleMeetups · Circles"],
    ["meetups_matchmaker", "HustleMeetups · Matchmaker"],
    ["roundup_sales",      "HustleRoundup · Sales"],
    ["suite_sales",        "HustleSuite · Sales"],
  ];

  const loadCtaSettings = () => {
    fetch(`${API}/api/hustlemeet-settings`)
      .then((r) => r.json())
      .then((d) => {
        const route = d.signup_route || "/signup";
        const vis   = d.cta_join_enabled || {};
        setCtaRoute(route);
        setCtaVis(vis);
        setCtaSaved({ route, vis });
      })
      .catch(() => { /* keep defaults */ });
  };

  const saveCta = async () => {
    setCtaSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/hustlemeet-settings`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signup_route:      ctaRoute.trim() || "/signup",
          cta_join_enabled:  ctaVis,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setCtaSaved({
        route: j.signup_route || ctaRoute,
        vis:   j.cta_join_enabled || ctaVis,
      });
      toast.success("Join CTA settings saved");
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setCtaSaving(false);
    }
  };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/platform/modules/admin`)
      .then((r) => r.json())
      .then((d) => {
        const rows = d.modules || [];
        setModules(rows);
        // seed drafts from server truth
        const seed = {};
        rows.forEach((m) => { seed[m.key] = { label: m.label, host: m.host || "", enabled: !!m.enabled }; });
        setDrafts(seed);
      })
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(loadCtaSettings, []);

  const isDirty = (key) => {
    const m = modules.find((x) => x.key === key);
    const d = drafts[key];
    if (!m || !d) return false;
    return d.label !== m.label || d.host !== (m.host || "") || d.enabled !== !!m.enabled;
  };

  const revert = (key) => {
    const m = modules.find((x) => x.key === key);
    if (!m) return;
    setDrafts((s) => ({ ...s, [key]: { label: m.label, host: m.host || "", enabled: !!m.enabled } }));
  };

  const save = async (key) => {
    const d = drafts[key];
    if (!d) return;
    const m = modules.find((x) => x.key === key);
    const patch = {};
    if (d.label !== m.label)      patch.label   = d.label.trim();
    if (d.host !== (m.host || ""))patch.host    = d.host.trim();
    if (d.enabled !== !!m.enabled)patch.enabled = d.enabled;
    if (Object.keys(patch).length === 0) return;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const r = await fetch(`${API}/api/platform/modules/${key}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setModules((ms) => ms.map((x) => (x.key === key ? { ...x, ...patch } : x)));
      try { window.dispatchEvent(new CustomEvent("hs:modules-changed")); } catch (_) { /* ignore */ }
      toast.success(`${key}: saved`);
    } catch (e) {
      toast.error(`${key}: ${e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const setField = (key, field, value) => {
    setDrafts((s) => ({ ...s, [key]: { ...s[key], [field]: value } }));
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950" data-testid="admin-global-header-page">
      <PlatformHeader
        brand={{ name: "HustleSuite Admin", to: "/admin" }}
        nav={[
          { to: "/admin", label: "Dashboard" },
          { to: "/admin/global-header", label: "Global Header" },
          { to: "/admin/modules", label: "Modules On/Off" },
        ]}
      />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-3">
          <Grid3x3 className="w-6 h-6 text-[#C05746]" strokeWidth={1.75} title="Global Header" />
          <h1 className="text-2xl sm:text-3xl font-black text-stone-900 dark:text-stone-100">
            Global Header · Other Modules
          </h1>
        </div>
        <p className="text-sm text-stone-700 dark:text-stone-300 mb-8 max-w-2xl">
          Each row below is one module in the platform. Rename the Menu Name (what visitors see in the dropdown),
          set its Salespage URL (where clicking takes them), and tick <em>Show</em> to include it in the dropdown.
          Changes go live the moment you click Save.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-stone-600 dark:text-stone-300" data-testid="loading">
            <Loader2 className="animate-spin" size={16} aria-hidden="true" /> Loading modules…
          </div>
        ) : (
          <div className="rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden bg-white dark:bg-stone-900 shadow-sm"
               data-testid="modules-table-wrapper">
            <div className="hidden md:grid grid-cols-[110px_1fr_1.4fr_90px_120px] gap-3 px-4 py-3 bg-stone-100 dark:bg-stone-800
                            text-[11px] uppercase tracking-widest font-bold text-stone-700 dark:text-stone-200 border-b border-stone-200 dark:border-stone-700">
              <div>DB Name</div>
              <div>Menu Name</div>
              <div>Salespage URL</div>
              <div className="text-center">Show</div>
              <div className="text-right">Save</div>
            </div>
            <ul className="divide-y divide-stone-200 dark:divide-stone-800">
              {modules.map((m) => {
                const d = drafts[m.key] || { label: m.label, host: m.host || "", enabled: !!m.enabled };
                const dirty = isDirty(m.key);
                return (
                  <li key={m.key}
                      data-testid={`module-row-${m.key}`}
                      className="grid grid-cols-1 md:grid-cols-[110px_1fr_1.4fr_90px_120px] gap-3 px-4 py-4 items-center">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: m.color || "#7c3aed" }}
                        aria-hidden="true"
                      />
                      <code className="text-[12px] text-stone-700 dark:text-stone-200 font-mono">{m.key}</code>
                    </div>
                    <Input
                      data-testid={`label-input-${m.key}`}
                      value={d.label}
                      onChange={(e) => setField(m.key, "label", e.target.value)}
                      placeholder="Menu name (e.g. HustleTravel)"
                      className="bg-white dark:bg-stone-950"
                    />
                    <Input
                      data-testid={`host-input-${m.key}`}
                      value={d.host}
                      onChange={(e) => setField(m.key, "host", e.target.value)}
                      placeholder="https://hustletravel.au"
                      className="bg-white dark:bg-stone-950 font-mono text-[12px]"
                    />
                    <label className="flex items-center justify-center cursor-pointer"
                           title={d.enabled ? "Visible in dropdown" : "Hidden from dropdown"}>
                      <input
                        type="checkbox"
                        data-testid={`enabled-checkbox-${m.key}`}
                        checked={d.enabled}
                        onChange={(e) => setField(m.key, "enabled", e.target.checked)}
                        className="h-5 w-5 rounded border-stone-300 dark:border-stone-600 text-[#C05746]
                                   focus:ring-[#C05746] cursor-pointer"
                        aria-label={`Show ${m.label} in Other Modules dropdown`}
                      />
                    </label>
                    <div className="flex items-center gap-1.5 justify-end">
                      {dirty && (
                        <Button
                          type="button"
                          onClick={() => revert(m.key)}
                          variant="ghost"
                          size="sm"
                          data-testid={`revert-btn-${m.key}`}
                          className="h-8 w-8 p-0"
                          title="Revert changes"
                        >
                          <RotateCcw size={14} aria-hidden="true" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={() => save(m.key)}
                        disabled={!dirty || saving[m.key]}
                        data-testid={`save-btn-${m.key}`}
                        size="sm"
                        className="h-8 px-3 bg-[#C05746] hover:bg-[#a04836] text-white disabled:opacity-40"
                        title={dirty ? "Save changes" : "No changes to save"}
                      >
                        {saving[m.key] ? <Loader2 className="animate-spin" size={13} aria-hidden="true" />
                          : dirty ? <><Save size={13} className="mr-1" aria-hidden="true" /> Save</>
                          : <><Check size={13} className="mr-1" aria-hidden="true" /> Saved</>}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── Sign-up CTA controls ──────────────────────────────── */}
        <section className="mt-12" data-testid="cta-join-section">
          <div className="flex items-center gap-3 mb-3">
            <UserPlus className="w-6 h-6 text-emerald-600" strokeWidth={1.75} title="Join CTA" />
            <h2 className="text-xl sm:text-2xl font-black text-stone-900 dark:text-stone-100">
              Join Hustle Membership · CTA
            </h2>
          </div>
          <p className="text-sm text-stone-700 dark:text-stone-300 mb-6 max-w-2xl">
            Choose which sales pages show the emerald <em>Join Hustle Membership</em> button, and
            edit the sign-up route the button links to (default <code>/signup</code>). Untick a
            row to hide the CTA on that page — the page still renders normally.
          </p>

          <div className="rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden bg-white dark:bg-stone-900 shadow-sm">
            <div className="px-4 py-4 border-b border-stone-200 dark:border-stone-800 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3 items-center">
              <label className="text-[11px] uppercase tracking-widest font-bold text-stone-700 dark:text-stone-200">
                Sign-up route
              </label>
              <Input
                data-testid="cta-signup-route-input"
                value={ctaRoute}
                onChange={(e) => setCtaRoute(e.target.value)}
                placeholder="/signup"
                className="bg-white dark:bg-stone-950 font-mono text-[13px]"
              />
            </div>
            <ul className="divide-y divide-stone-200 dark:divide-stone-800">
              {CTA_PAGES.map(([key, label]) => (
                <li key={key}
                    data-testid={`cta-row-${key}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_100px] gap-3 px-4 py-3 items-center">
                  <span className="text-sm text-stone-800 dark:text-stone-100">{label}</span>
                  <label className="flex items-center justify-center cursor-pointer"
                         title={ctaVis[key] ? "Join CTA shown" : "Join CTA hidden"}>
                    <input
                      type="checkbox"
                      data-testid={`cta-checkbox-${key}`}
                      checked={!!ctaVis[key]}
                      onChange={(e) => setCtaVis((s) => ({ ...s, [key]: e.target.checked }))}
                      className="h-5 w-5 rounded border-stone-300 dark:border-stone-600 text-emerald-600
                                 focus:ring-emerald-600 cursor-pointer"
                      aria-label={`Show Join CTA on ${label}`}
                    />
                  </label>
                </li>
              ))}
            </ul>
            <div className="px-4 py-3 border-t border-stone-200 dark:border-stone-800 flex items-center justify-end gap-2">
              {ctaDirty && (
                <Button
                  type="button"
                  onClick={() => { setCtaRoute(ctaSaved.route); setCtaVis(ctaSaved.vis); }}
                  variant="ghost"
                  size="sm"
                  data-testid="cta-revert-btn"
                  className="h-8 px-3"
                  title="Revert changes"
                >
                  <RotateCcw size={14} className="mr-1" aria-hidden="true" /> Revert
                </Button>
              )}
              <Button
                type="button"
                onClick={saveCta}
                disabled={!ctaDirty || ctaSaving}
                data-testid="cta-save-btn"
                size="sm"
                className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                title={ctaDirty ? "Save CTA settings" : "No changes to save"}
              >
                {ctaSaving ? <Loader2 className="animate-spin" size={13} aria-hidden="true" />
                  : ctaDirty ? <><Save size={13} className="mr-1" aria-hidden="true" /> Save CTA</>
                  : <><Check size={13} className="mr-1" aria-hidden="true" /> Saved</>}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
